import React, { useState, useEffect } from "react";
import { Box, Typography, Grid2 } from "@mui/material";
import HierarchicalSelector from "../../Common/HierarchicalSelector";
import DuplicateSignal from "../../Common/DuplicateSignal";
import YColumnsTooltip from "../../Common/YColumnsTooltip";
import { extractEntities, getShuffledColors } from "../../Common/CommonFunctions";

const SignalsTab = ({
  hierarchyInputData,
  batteryID,
  selectedEntities,
  onEntitiesChange,
  isLoading = false,
}) => {
  const [selectedChips, setSelectedChips] = useState([]);
  const [showDuplicateError, setShowDuplicateError] = useState(false);

  // Update chips when entities change (from parent or Series tab)
  useEffect(() => {
    const chips = selectedEntities.map(entity => entity.raw);
    setSelectedChips(chips);
  }, [selectedEntities]);

  const handleSelectionChange = (chips) => {
    const extractedEntities = extractEntities(batteryID, chips, hierarchyInputData);
    const shuffledColors = getShuffledColors();

    // map of existing entity -> { color, YAxis }
    const existingEntityMap = new Map(
      selectedEntities.map((entity) => [
        `${entity.id}_${entity.metric}`,
        { color: entity.color, yaxis: entity.yaxis }
      ])
    );

    // set of colors already used
    const usedColors = new Set(selectedEntities.map((e) => e.color));

    let colorIndex = 0;
    const entitiesWithExtras = extractedEntities.map((entity) => {
      const uniqueKey = `${entity.id}_${entity.metric}`;

      if (existingEntityMap.has(uniqueKey)) {
        // keep old color and YAxis
        const { color, yaxis } = existingEntityMap.get(uniqueKey);
        return { ...entity, color, yaxis };
      }

      // find the next available unused color
      let color;
      while (colorIndex < shuffledColors.length) {
        const candidate = shuffledColors[colorIndex % shuffledColors.length];
        colorIndex++;
        if (!usedColors.has(candidate)) {
          color = candidate;
          usedColors.add(color); // mark it as taken
          break;
        }
      }

      // fallback in case palette is smaller than entities
      if (!color) {
        color = shuffledColors[colorIndex % shuffledColors.length];
        colorIndex++;
      }

      return { ...entity, color, yaxis: "Y1" };
    });
    onEntitiesChange(entitiesWithExtras);
  };

  return (
    <Grid2
      container
      flexDirection={'column'}
      justifyContent={'space-between'}
      sx={{ height: 'inherit', position: 'relative' }}
    >
      <Grid2 container gap={0.5} flexDirection={'column'}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Grid2 container gap={0.5} alignItems={'center'}>
            <Typography variant="fw400s12c494949" sx={{ color: '#15191E', lineHeight: '24px' }}>
              Y-Columns
            </Typography>
            <YColumnsTooltip />
          </Grid2>
        </Box>
        <HierarchicalSelector
          data={hierarchyInputData}
          selectedChips={selectedChips}
          onSelectionChange={handleSelectionChange}
          onDuplicateError={() => setShowDuplicateError(true)}
          placeholder="Define entities & corresponding signals"
          allowDuplicates={false}
          selectionMode="checklist"
          className="w-full"
          maxHeight={'240px'}
          isLoading={isLoading}
        />
      </Grid2>
      {showDuplicateError && <DuplicateSignal onClose={() => setShowDuplicateError(false)} />}
    </Grid2>
  );
};

export default SignalsTab;
